import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import './datatable.css';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DIAS_CAB = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

function parseYmd(ymd?: string): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatBr(ymd?: string): string {
  const d = parseYmd(ymd);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR');
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export interface DatePickerProps {
  value?: string;
  onChange?: (ymd: string) => void;
  label?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  width?: string | number;
  error?: string;
}

export function DatePicker({
  value,
  onChange,
  label,
  placeholder = 'Selecionar data',
  min,
  max,
  disabled = false,
  clearable = false,
  className = '',
  width = '100%',
  error,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selected = parseYmd(value);
  const [view, setView] = useState(() => selected ?? new Date());
  const containerRef = useRef<HTMLDivElement>(null);
  const minDate = parseYmd(min);
  const maxDate = parseYmd(max);

  useEffect(() => {
    if (isOpen) setView(selected ?? new Date());
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const cells = useMemo(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out: Array<{ date: Date; inMonth: boolean } | null> = [];
    for (let i = 0; i < startPad; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      out.push({ date: new Date(year, month, d), inMonth: true });
    }
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  const isDisabledDay = (d: Date) => {
    if (minDate) {
      const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const b = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      if (a < b) return true;
    }
    if (maxDate) {
      const a = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const b = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      if (a > b) return true;
    }
    return false;
  };

  const pick = (d: Date) => {
    if (isDisabledDay(d)) return;
    onChange?.(toYmd(d));
    setIsOpen(false);
  };

  const today = new Date();

  const widthStyle =
    typeof width === 'number'
      ? { width, minWidth: width, flexShrink: 0 as const }
      : { width, minWidth: width === '100%' ? undefined : width, flexShrink: 0 as const };

  return (
    <div
      className={`dd-wrapper dp-wrapper ${className}${error ? ' dd-error' : ''}`}
      ref={containerRef}
      style={widthStyle}
    >
      {label && <label className="form-label">{label}</label>}

      <button
        type="button"
        className={`dd-trigger${isOpen ? ' dd-trigger-open' : ''}${disabled ? ' dd-trigger-disabled' : ''}`}
        onClick={() => { if (!disabled) setIsOpen((o) => !o); }}
        disabled={disabled}
      >
        <div className="dd-trigger-content">
          <span className="dd-trigger-icon"><Calendar size={14} /></span>
          {selected ? (
            <span className="dd-selected-label">{formatBr(value)}</span>
          ) : (
            <span className="dd-placeholder">{placeholder}</span>
          )}
        </div>
        <div className="dd-trigger-actions">
          {clearable && selected && (
            <span
              className="dd-clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.('');
              }}
              title="Limpar"
            >
              <X size={13} />
            </span>
          )}
        </div>
      </button>

      {error && <span className="form-error-text">{error}</span>}

      {isOpen && (
        <div className="dd-panel dd-align-left dp-panel">
          <div className="dp-nav">
            <button
              type="button"
              className="dp-nav-btn"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              aria-label="Mês anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="dp-nav-title">
              {MESES[view.getMonth()]} {view.getFullYear()}
            </div>
            <button
              type="button"
              className="dp-nav-btn"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              aria-label="Próximo mês"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="dp-weekdays">
            {DIAS_CAB.map((d, i) => (
              <span key={`${d}-${i}`} className="dp-weekday">{d}</span>
            ))}
          </div>

          <div className="dp-grid">
            {cells.map((cell, idx) => {
              if (!cell) {
                return <span key={`e-${idx}`} className="dp-day dp-day-empty" />;
              }
              const { date } = cell;
              const disabledDay = isDisabledDay(date);
              const selectedDay = selected ? sameDay(date, selected) : false;
              const isToday = sameDay(date, today);
              return (
                <button
                  key={toYmd(date)}
                  type="button"
                  className={[
                    'dp-day',
                    selectedDay ? 'dp-day-selected' : '',
                    isToday && !selectedDay ? 'dp-day-today' : '',
                    disabledDay ? 'dp-day-disabled' : '',
                  ].filter(Boolean).join(' ')}
                  disabled={disabledDay}
                  onClick={() => pick(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="dp-footer">
            <button
              type="button"
              className="dp-today-btn"
              onClick={() => {
                const t = new Date();
                if (!isDisabledDay(t)) pick(t);
                else setView(new Date(t.getFullYear(), t.getMonth(), 1));
              }}
            >
              Hoje
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
